import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import avatar from "../../../../../assets/img/noAvatar.png";
import { getAgeByYearMonthDay, formatName, formatPadStartNumber, convertToFullDate, formatDate } from "utils/format";
import TextInput from '../../../../components/TextInput';
import Dropdown from '../../../../components/Dropdown';
import MonthRangePicker from '../../../../components/MonthRangePicker';
import CustomTextarea from '../../../../components/CustomTextarea';
import CustomButton from '../../../../components/CustomButton';
import CustomToast from '../../../../components/CustomToast';
import { useSnackbar } from 'notistack';
import { STAFF_TYPE } from "../../../../constants/index";
import CustomIconButton from '../../../../components/CustomIconButton';
import { getDetailTalent, updateTalentResume } from 'reducers/companyTalent';
import { downloadFileAttachment } from 'utils/util';
import FileItem from '../../../../components/FileItem';
import i18n from 'i18next';

const BasicInformationSection = ({talentRanks, talentTypes, formData, handleBlurEmailInput, handleChangeForm, handleBlurInput, toggleTalentTagPanel, handleSelectTag, setFormData}) => {
  const { selectedTalent, talentTagsSelected, actionStatus, actionName } = useSelector((state: any) => state.companyTalent);
  const talentTags = talentTagsSelected?.length > 0 && talentTagsSelected.filter(tag => tag?.selected === true);
  const {staffInfo} = useSelector((state: any) => state.currentUser);
  const [isRelatedLinkEditing, setIsRelatedLinkEditing] = useState(false);
  const [relatedLinks, setRelatedLinks] = useState([]);
  const [relatedLinkErrors, setRelatedLinkErrors] = useState([]);
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showToast = (title: string, message: string, variant: string) => {
    enqueueSnackbar("I love snacks.", {
      autoHideDuration: 7000,
      content: (key) => (
        <CustomToast title={title} message={message} variant={variant} snackbarKey={key} closeSnackbar={closeSnackbar} />
      ),
    });
  };

  const handleDownloadFileAttachment = async (url: string, filename?: string) => {
    try {
      await downloadFileAttachment(url, filename);
    } catch (error) {
      showToast("メッセージ", i18n.t('MSG_GLOBAL_FILE_NOT_EXIST_OR_DELETED'), "error");
    }
  };

  const handleChangeLinkData = (e:any, index:number) => {
    const { name, value } = e.target;

    setRelatedLinks((prevLinks) => {
      const newLinks = [...prevLinks];

      newLinks[index] = {
        ...newLinks[index],
        [name]: value,
      };

      return newLinks;
    });
  }

  const handleAddRelatedLink = () => {
    if(relatedLinks && relatedLinks?.length < 3) {
      const newRelatedLinks = [...relatedLinks, {id: null, file_url: "", file_name: ""}];
      setRelatedLinks(newRelatedLinks);
    }
  }

  const handleDeleteLink = (e:any, index:number) => {
    e.preventDefault();
    if(relatedLinks) {
      const newRelatedLinks = relatedLinks.filter((_:any, i:number) => i !== index);
      const newRelatedLinkErrors = relatedLinkErrors.filter((_:any, i:number) => i !== index);
      setRelatedLinks(newRelatedLinks);
      setRelatedLinkErrors(newRelatedLinkErrors);
    }
  }

  const handleValidateLinks = () => {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4)
      '(\\:\\d+)?' + // port
      '(\\/[\\w\\-%.~+]*)*' + // path (allows A-Z, a-z, 0-9, _)
      '(\\?[;&\\w\\-%.~+=]*)?' + // query string
      '(\\#[\\w\\-%.~+=]*)?$', // fragment
      'i'
    );
  
    const newErrors = relatedLinks.map((link) => {
      if (!link.file_url || link.file_url.trim() === "") {
        return "リンクを入力してください";
      }
      if (!urlPattern.test(link.file_url)) {
        return "有効なURLを入力してください";
      }
      return "";
    });
  
    setRelatedLinkErrors(newErrors);
  
    return newErrors.every((error) => error === "");
  };

  const submitTalentResumes = async () => {
    const validateFlag = handleValidateLinks();
    if(!validateFlag) {
      return
    }

    const data = {
      talent_company_id: selectedTalent?.id,
      resumes: relatedLinks?.filter((link:any) => link.file_url.trim())
    }
    dispatch(updateTalentResume({requestBody: data}));
  }

  const handleCloseEditingMode = () => {
    setFormData((prev) => ({
      ...prev,
      id: null,
      related_links: prev.related_links.filter((item:any) => item.id || item.file_url).length > 0 ? prev.related_links.filter((item:any) => item.id || item.file_url) : [{id: null, file_url: "", file_name: ""}]
    }));
    setIsRelatedLinkEditing(false);
  }

  useEffect(() => {
    if(formData?.related_links && formData?.related_links.length) {
      setRelatedLinks(formData?.related_links)
    } else {
      setRelatedLinks([{id: null, file_url: "", file_name: ""}])
    }
  }, [formData?.related_links]);

  useEffect(() => {
    if(isRelatedLinkEditing) {
      if(formData?.related_links && formData.related_links.length > 0) {
        setRelatedLinkErrors(new Array(formData.related_links.length).fill(''));
      } else {
        setRelatedLinkErrors(new Array(1).fill(''));
      }
    }
  }, [isRelatedLinkEditing])

  useEffect(() => {
    return () => {
      if(selectedTalent?.id)
        setIsRelatedLinkEditing(false);
    }
  }, [selectedTalent?.id])

  useEffect(() => {
    if (actionStatus === 'fulfilled' && actionName === 'updateTalentResume') {
      setIsRelatedLinkEditing(false)
    }
  }, [actionStatus, actionName]);
  
  return (
    <>
      <div className="p-4 rounded-lg mb-4 border border-[#E0E0E0] flex items-center gap-6">
        <div className="shrink-0 flex-1 flex flex-col gap-1">
          <div className="flex gap-4 items-center">
            <img
              alt="avatar"
              className="w-[64px] h-[64px] shrink-0 rounded-full"
              src={
                selectedTalent?.talent?.avatar
                  ? selectedTalent?.talent?.avatar
                  : avatar
              }
            />
            <div>
              <div className="flex items-center gap-3">
                <h5 className="text-base font-bold alu-text-base-2">
                  {formatName(
                    selectedTalent?.talent?.first_name,
                    selectedTalent?.talent?.last_name,
                  )}
                </h5>
                {!!selectedTalent?.id && (
                  selectedTalent?.is_active ? (
                    <span className="rounded-2xl bg-[#e0e0e0] text-[#343a3d] text-sm px-2 py-0.5 shrink-0">{selectedTalent?.status}</span>
                  ) : (
                    <p className="alu-text-base-2 text-base shrink-0">（退会済み）</p>
                  )
                )}
              </div>
              <p className="text-xs alu-text-base-1">
                {formatName(
                  selectedTalent?.talent?.first_name_kana,
                  selectedTalent?.talent?.last_name_kana,
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-[64px]"></div>
            <div className="flex flex-col gap-1 flex-1">
              <div className="flex gap-2 text-sm leading-[21px]">
                <p className="shrink-0 min-w-[4.5rem] alu-text-base-1">現職</p>
                <p className="flex-1 alu-text-base-2">{selectedTalent?.talent?.current_career?.description || 'ー'}</p>
              </div>
              <div className="flex gap-2 text-sm leading-[21px]">
                <p className="shrink-0 min-w-[4.5rem] alu-text-base-1">生年月日</p>
                <p className="flex-1 alu-text-base-2">
                  {convertToFullDate(selectedTalent?.talent?.birth_year, selectedTalent?.talent?.birth_month, selectedTalent?.talent?.birth_day, false)}
                </p>
              </div>
              <div className="flex gap-2 text-sm leading-[21px]">
                <p className="shrink-0 min-w-[4.5rem] alu-text-base-1">年齢</p>
                <p className="flex-1 alu-text-base-2">
                  {getAgeByYearMonthDay(selectedTalent?.talent?.birth_year, selectedTalent?.talent?.birth_month, selectedTalent?.talent?.birth_day)}
                </p>
              </div>
              <div className="flex gap-2 text-sm leading-[21px]">
                <p className="shrink-0 min-w-[4.5rem] alu-text-base-1">居住地</p>
                <p className="flex-1 alu-text-base-2">{selectedTalent?.talent?.city || 'ー'}</p>
              </div>
              <div className="flex gap-2 text-sm leading-[21px]">
                <p className="shrink-0 min-w-[4.5rem] alu-text-base-1">性別</p>
                <p className="flex-1 alu-text-base-2">{selectedTalent.talent?.gender ? selectedTalent.talent?.gender : 'ー'}</p>
              </div>
              <div className="flex gap-2 text-sm leading-[21px]">
                <p className="shrink-0 min-w-[4.5rem] alu-text-base-1">ID</p>
                <p className="flex-1 alu-text-base-2">{selectedTalent.talent?.id ? formatPadStartNumber(selectedTalent.talent?.id, 6) : 'ー'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="min-w-[14rem] flex flex-col gap-6">
          <div className="py-2 px-4 flex-1 rounded-lg bg-[#F7F7F7]">
            <p className="text-sm alu-text-base-1 text-center">副業意欲</p>
            <p className="text-sm alu-text-base-2 font-bold text-center">
              {selectedTalent?.talent?.work_parttime ? selectedTalent?.talent?.work_parttime : 'ー'}
            </p>
          </div>
          <div className="py-2 px-4 flex-1 rounded-lg bg-[#F7F7F7]">
            <p className="text-sm alu-text-base-1 text-center">転職意欲</p>
            <p className="text-sm alu-text-base-2 font-bold text-center">
              {selectedTalent?.talent?.change_job ? selectedTalent?.talent?.change_job: 'ー' }
            </p>
          </div>
        </div>
      </div>
    
    <div className="mb-8 px-2">
      <h5 className="text-sm alu-text-base-2 font-bold mb-2">基本情報</h5>
      <div className="flex items-center mb-2">
        <p className="min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">メール</p>
        <p className="text-right flex-1 text-base alu-text-base-2">
          <TextInput
            size="md"
            type="text"
            id="email"
            name="email"
            value={formData.email}
            maxLength={201}
            onChange={handleChangeForm}
            onBlur={handleBlurEmailInput}
            onKeyDown={(e: any) => e.key === "Enter" && handleBlurEmailInput(e)}
            disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL}
          />
        </p>
      </div>
      <div className="flex items-center mb-2">
        <p className="min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">種別</p>
        <div className="flex-1">
          <Dropdown
            inputWidth="full"
            options={talentTypes}
            placeholder="選択してください"
            value={formData.type}
            onChange={(value: any) => {
              setFormData((prev) => ({
                ...prev,
                id: selectedTalent?.id, 
                type: value,
              }));
            }}
            disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL}
          />
        </div>
      </div>
    </div>

    <div className="mb-8 px-2">
      <h5 className="text-sm alu-text-base-2 font-bold mb-2">管理項目</h5>
      <div className="flex mb-2">
        <p className="min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">タグ</p>
        <div className="flex-1 flex flex-wrap items-center gap-2 text-sm alu-text-base-2">
          {!!selectedTalent?.id && talentTags?.length > 0 &&
            [...talentTags]
              .sort((a, b) => (a?.title || '').localeCompare(b?.title || ''))
              .map((tag, idx) => (
                <div
                  key={idx}
                  className="px-2 py-[0.125rem] text-center rounded-sm bg-[#F7F7F7] hover:cursor-pointer hover:opacity-90"
                  onClick={() => handleSelectTag(tag)}
                >
                  {tag?.title}
                </div>
              ))
          }
          <CustomButton variant="text" onClick={() => toggleTalentTagPanel(true)} disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL}>
            編集
          </CustomButton>
        </div>
      </div>
      <div className="flex mb-2">
        <p className="min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">登録リンクコード</p>
        <p className="text-left flex-1 text-sm alu-text-base-2">
          {selectedTalent?.talent_temp?.register_code ? selectedTalent?.talent_temp?.register_code : 'ー'} 
        </p>
      </div>
      <div className="flex items-center mb-2">
        <p className="min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">採用ランク</p>
        <div className="flex-1">
          <Dropdown
            inputWidth="full"
            options={talentRanks}
            placeholder="選択してください"
            value={formData.recruitment_rank}
            onChange={(value: any) => {
              setFormData((prev) => ({
                ...prev,
                id: selectedTalent?.id, 
                recruitment_rank: value,
              }));
            }}
            disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL}
          />
        </div>
      </div>
      <div className="flex items-center mb-2">
        <p className="min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">エージェント情報</p>
        <p className="text-right flex-1 text-base alu-text-base-2">
          <TextInput
            size="md"
            type="text"
            id="agent_info"
            name="agent_info"
            value={formData.agent_info}
            maxLength={201}
            onChange={handleChangeForm}
            onBlur={handleBlurInput}
            onKeyDown={(e: any) => e.key === "Enter" && setFormData((prev) => ({
              ...prev,
              id: selectedTalent?.id
            }))}
            disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL}
          />
        </p>
      </div>
      <div className="flex items-center mb-2">
        <p className="min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">オーナー期限</p>
        <div className="w-full">
          <MonthRangePicker
            anchor="top"
            placeholderStart="開始年月"
            placeholderEnd="終了年月"
            type={3}
            startMonth={formData.ownership_period_start_date}
            endMonth={formData?.ownership_period_end_date ? formData?.ownership_period_end_date : ''}
            onChange={(start: any, end: any) => {
              setFormData((prev) => ({ ...prev, id: selectedTalent?.id, ownership_period_start_date: start, ownership_period_end_date: end }))
            }}
            disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL}
          />
        </div>
      </div>
      <div className="flex mb-2">
        <p className="min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">備考</p>
        <p className="text-right flex-1 text-base alu-text-base-2">
          <CustomTextarea
            size="md"
            id="note"
            name="note"
            maxLines={2}
            maxLength={2000}
            value={formData.note}
            onChange={handleChangeForm}
            onBlur={handleBlurInput}
            disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL}
          />
        </p>
      </div>
      <div className={`${isRelatedLinkEditing ? 'p-4 border border-[#FF861A] rounded' : 'mb-4'}`}>
        <div className={`flex`}>
          <p className={`${!isRelatedLinkEditing ? 'min-w-[9.5rem]' : 'min-w-[8.5625rem]'} shrink-0 text-sm alu-text-base-1 mt-1`}>関連リンク</p>
          <div className='w-full'>
            {isRelatedLinkEditing ? (
                <>
                {relatedLinks?.map((relatedLink, index) => (
                  <div key={index} className={`flex gap-[0.625rem] border-b border-[#E0E0E0] pb-6 ${relatedLinks?.length - 1 === index ? '' : 'mb-6' }`}>
                    <div className='flex flex-col w-full'>
                      <CustomTextarea
                        size="md" id="file_url" name="file_url" value={relatedLink.file_url} placeholder="リンク"
                        onChange={(e:any) => handleChangeLinkData(e, index)} customStyles="px-3"
                        error={relatedLinkErrors && relatedLinkErrors[index] ? true : false} errorText=""
                      />
                      {relatedLinkErrors && relatedLinkErrors[index] && <p className='text-sm alu-text-error mb-2'>＊{relatedLinkErrors[index]}</p>}
                      <TextInput 
                        size="md" type="text" id="file_name" name="file_name" value={relatedLink.file_name} placeholder="テキスト"
                        onChange={(e:any) => handleChangeLinkData(e, index)} maxLength={100}
                      />
                    </div>
                    <CustomIconButton customStyle='shrink-0' onClick={(e:any) => handleDeleteLink(e, index)} disabled={relatedLinks?.length == 1 && !relatedLinks[0].id}>
                      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                        <path d="M200-440v-80h560v80H200Z"/>
                      </svg>
                    </CustomIconButton>
                  </div>
                ))}
                {relatedLinks?.length < 3 && <button type="button" className={`${relatedLinks.length > 0 ? 'mt-4' : ''} flex items-center gap-1 px-2 py-1 hover:bg-[#FFF3E5] rounded`} onClick={handleAddRelatedLink}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <g clip-path="url(#clip0_6374_496)">
                      <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="#FF861A"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_6374_496">
                        <rect width="24" height="24" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                  <span className="alu-text-title text-sm">リンクを追加</span>
                </button>}
                <div className='flex items-center gap-4 mt-6'>
                  <CustomButton customStyle='w-full' variant="outline" size="md" onClick={handleCloseEditingMode}>キャンセル</CustomButton>
                  <CustomButton customStyle='w-full' size="md" onClick={submitTalentResumes}>保存</CustomButton>
                </div>
                </>
              ) : (
                  formData.related_links && formData.related_links.filter(item => item.id || item.file_url).length > 0 &&
                    formData.related_links.filter(item => item.id || item.file_url).map((link: any, index: number) => (
                      <a 
                        key={index} 
                        href={link.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`max-w-fit flex items-center gap-[0.375rem] text-sm group ${index !== formData.related_links.filter(item => item.id || item.file_url).length - 1 ? "mb-2" : "mb-1"}`}
                      >
                        <span className='shrink-0 group-hover:text-[#FF861A] transition-colors'>
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <mask id="mask0_6895_15288" style={{maskType: "alpha"}} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                              <rect width="20" height="20" fill="#D9D9D9"/>
                            </mask>
                            <g mask="url(#mask0_6895_15288)">
                              <path d="M6.625 17.5C5.48611 17.5 4.51389 17.0972 3.70833 16.2917C2.90278 15.4861 2.5 14.5139 2.5 13.375C2.5 12.8194 2.60417 12.2917 2.8125 11.7917C3.02083 11.2917 3.31944 10.8472 3.70833 10.4583L6.5 7.6875L7.66667 8.85417L4.875 11.6458C4.63889 11.8819 4.46181 12.1493 4.34375 12.4479C4.22569 12.7465 4.16667 13.0556 4.16667 13.375C4.16667 14.0556 4.40625 14.6354 4.88542 15.1146C5.36458 15.5937 5.94444 15.8333 6.625 15.8333C6.94444 15.8333 7.25694 15.7743 7.5625 15.6562C7.86806 15.5382 8.13889 15.3611 8.375 15.125L11.1458 12.3333L12.3333 13.5208L9.54167 16.2917C9.15278 16.6806 8.70833 16.9792 8.20833 17.1875C7.70833 17.3958 7.18056 17.5 6.625 17.5ZM8.27083 12.9167L7.08333 11.7292L11.7292 7.08333L12.9167 8.27083L8.27083 12.9167ZM13.5 12.3333L12.3333 11.1458L15.125 8.375C15.3611 8.13889 15.5347 7.875 15.6458 7.58333C15.7569 7.29167 15.8125 6.98611 15.8125 6.66667C15.8125 5.97222 15.5764 5.38194 15.1042 4.89583C14.6319 4.40972 14.0486 4.16667 13.3542 4.16667C13.0347 4.16667 12.7257 4.22569 12.4271 4.34375C12.1285 4.46181 11.8611 4.63889 11.625 4.875L8.85417 7.66667L7.66667 6.5L10.4583 3.70833C10.8472 3.31944 11.2917 3.02083 11.7917 2.8125C12.2917 2.60417 12.8194 2.5 13.375 2.5C14.5139 2.5 15.4826 2.90278 16.2812 3.70833C17.0799 4.51389 17.4792 5.49306 17.4792 6.64583C17.4792 7.1875 17.3785 7.70833 17.1771 8.20833C16.9757 8.70833 16.6806 9.15278 16.2917 9.54167L13.5 12.3333Z" fill="currentColor"/>
                            </g>
                          </svg>
                        </span>
                        <p className='max-w-[438px] truncate underline underline-offset-2 alu-text-base-2 group-hover:text-[#FF861A] transition-colors'>
                          {link.file_name ? link.file_name : link.file_url}
                        </p>
                      </a>
                    ))
              )
          
            }
            {!isRelatedLinkEditing && <CustomButton variant="text" onClick={() => setIsRelatedLinkEditing(prev => !prev)} disabled={!selectedTalent?.is_active || staffInfo?.type == STAFF_TYPE.GENERAL}>
              編集
            </CustomButton>}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {["RESUME", "CV", "OTHER"].map((item) => {
          let file = null;
          const uploadedFiles = selectedTalent?.uploaded_files || [];
          if(uploadedFiles.length > 0) {
            file = uploadedFiles.find((file: any) => file?.type === item);
          }

          return (
            <div key={item} className={`flex items-start ${item !== "OTHER" ? "mb-2" : ""}`}>
              <p className="mt-1 min-w-[9.5rem] shrink-0 text-sm alu-text-base-1">{item === "RESUME" ? "履歴書" : item === "CV" ? "職務経歴書" : "その他のファイル"}</p>
              {file ? <FileItem file={file} onFileClick={() => handleDownloadFileAttachment(file?.file_path, file?.file_name)} showDeleteButton={false} displayBorder={false} /> : 'ー'}
            </div>
          )
        })}
      </div>
    </div>
    </>
  )
}

export default BasicInformationSection